import { ActionSequence } from 'src/lib/ActionSequence';
import i18n from 'src/lib/i18n/i18n';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import { logoutFromNative } from 'src/lib/native/native.util';
import { isPaymentAvailable } from 'src/lib/iap/iap.util';
import { NativePromoOrigin } from 'src/replicant/native';
import * as Native from 'src/replicant/native';
import {
  getNativeAppUpdatePromptType,
  guessInstalledNativeAppPlatform,
  isNativePromoAvailable,
} from './native.util';
import StateObserver, { user, now } from 'src/StateObserver';
import { PinchType, SupportedOrigins } from '../shop/Helpers';
import { getProcessedMatch2Rewards } from 'src/replicant/match2/rewards/match2-rewards.getters';
import { openMatch2RewardsPopup } from '../minigame/match2-timestep/rewards/match2-rewards.utils';
import { DeepLink } from '../launch/deepLinks';
import { getRewardsId } from 'src/replicant/native/native.getters';
import { track } from 'src/lib/analytics/utils';
import * as Promo from 'src/replicant/promo';

export const startLogoutSequence = () =>
  ActionSequence.start(
    [
      async function showConfirmationPopup() {
        return !(await openPopupPromise('popupConfirmation', {
          title: i18n('native.logout.confirmation.title'),
          message: i18n('native.logout.confirmation.message'),
          buttonOkay: i18n('native.logout.confirmation.ok'),
        }));
      },

      async function logout() {
        await logoutFromNative();
        return false;
      },
    ],
    'Logout',
  );

export const startInstallNativeSequence = async (
  opts: InstallNativeSequenceArgs,
) => {
  // Skip if user already has payments available (e.g. LINE MiniApp, native app)
  if (isPaymentAvailable(StateObserver.getState())) return;

  // Skip in the case of already having the native app installed (or predicted at least) - despite the session being on web
  if (guessInstalledNativeAppPlatform() !== null) return;

  // Skip if native promo is not available
  if (!isNativePromoAvailable()) {
    return;
  }

  // Send analytics
  track('AppPromoPopupOpen', { feature: opts.originFeature });

  await StateObserver.invoke.installRewardWasShown();

  // show Match2 Play on App popup instead of the normal native popups for the Match2 group
  StateObserver.invoke.setMatch2PlayOnAppCooldownTimestamp();

  await openPopupPromise('popupMatch2PlayOnApp', {
    useClickOutside: true,
    origin: mapFeatureToDialogOrigin(opts.originFeature),
    ...opts,
  });
};

export type InstallNativeSequenceArgs =
  | {
      originFeature: Exclude<NativePromoOrigin, 'low_gem_balance'>;
      pinch?: PinchType;
    }
  | {
      originFeature: 'low_gem_balance';
      pinch?: PinchType;
      feature: SupportedOrigins;
      gemsNeeded?: number;
      additionalContext?: DeepLink[];
    };

/**
 * Native app reward install sequence
 */
export async function startInstallAppRewardSequence(
  origin: PopupDialogOrigin | undefined,
): Promise<boolean> {
  const { user } = StateObserver.getState();

  // Do we have non claimed reward?
  if (Native.getters.hasAppInstallReward(user)) {
    /* Show Match2 rewards popup and grant extra booster rewards for Match2 users
     * (Already included base native reward)
     */
    const now = StateObserver.now();
    const rewards = getProcessedMatch2Rewards(getRewardsId(user), now);
    await openMatch2RewardsPopup({ rewards, origin });
    await StateObserver.invoke.claimMatch2InstallReward(now);
  }

  return false;
}

/**
 * Show update app reminder popup, if needed
 */
export async function showUpdateAppReminder(origin: 'launch') {
  // Get update type - it will return 'skip' if not in native app
  const updateType = getNativeAppUpdatePromptType();
  if (updateType === 'skip') return;

  // For hard updates, show force update popup (blocking, no incentive tracking)
  if (updateType === 'hard') {
    await openPopupPromise('popupUpdateApp', {
      hardUpdate: true,
      origin,
    });
    return;
  }

  // Soft update logic with promo cooldowns
  const promo24Ready = Promo.getters.isPromoReady(
    user(),
    'updateAppReminder24h',
    now(),
  );

  const promo72Ready = Promo.getters.isPromoReady(
    user(),
    'updateAppReminder72h',
    now(),
  );

  // Only show if at least one promo is ready
  if (!promo24Ready && !promo72Ready) return;

  // If promo 24h is ready, mark as shown
  if (promo24Ready) {
    await StateObserver.invoke.promoWasShown('updateAppReminder24h');
  }

  // If promo 72h is ready, mark as shown
  if (promo72Ready) {
    await StateObserver.invoke.promoWasShown('updateAppReminder72h');
  }

  // Mark that user has seen the incentivised update promo
  await StateObserver.invoke.incentivisedUpdateWasShown();

  // Show incentivised update popup for soft updates
  await openPopupPromise('popupUpdateAppIncentivised', {
    hardUpdate: false,
    origin,
  });
}

/**
 * Check and grant incentivised update reward if eligible
 * Called during launch sequence after user updates their app
 */
export async function startIncentivisedUpdateRewardSequence(
  origin: PopupDialogOrigin | undefined,
): Promise<boolean> {
  // Skip if user hasn't seen the promo yet
  if (!Native.getters.hasSeenIncentivisedUpdatePromo(user())) {
    return false;
  }

  // Skip if user already claimed the reward
  if (Native.getters.hasClaimedIncentivisedUpdateReward(user())) {
    return false;
  }

  // Check if user has updated (current version is now good)
  const updateType = getNativeAppUpdatePromptType();
  if (updateType !== 'skip') {
    // User still needs to update
    return false;
  }

  // User has updated! Mark as received and show reward popup
  await StateObserver.invoke.markIncentivisedUpdateRewardReceived();

  await openPopupPromise('popupUpdateAppIncentivisedReward', {
    origin,
  });

  // Claim the reward
  await StateObserver.invoke.claimIncentivisedUpdateReward();

  return false;
}
